
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-fraudeye-primary">
      <div className="container max-w-4xl px-4 py-12">
        <Button variant="outline" size="sm" className="mb-8" asChild>
          <Link to="/">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Link>
        </Button>
        
        <div className="space-y-8">
          <div>
            <h1 className="text-3xl font-bold mb-4">Terms of Service</h1>
            <p className="text-muted-foreground">Last updated: April 14, 2025</p>
          </div>
          
          <section className="space-y-4">
            <h2 className="text-xl font-semibold">1. Agreement to Terms</h2>
            <p>
              These Terms of Service ("Terms") constitute a legally binding agreement between you ("User" or "you") and FraudEye ("Company", "we", or "us") governing your access to and use of the FraudEye platform, including any associated mobile applications (collectively, the "Service").
            </p>
            <p>
              By accessing or using the Service, you agree to be bound by these Terms. If you disagree with any part of these Terms, you may not access the Service.
            </p>
          </section>
          
          <section className="space-y-4">
            <h2 className="text-xl font-semibold">2. Description of Service</h2>
            <p>
              FraudEye provides fraud detection and content verification services, including but not limited to text analysis, image verification, video analysis, voice analysis, document verification, and plagiarism detection.
            </p>
            <p>
              We reserve the right to modify, suspend, or discontinue the Service (or any part thereof) at any time, with or without notice. We shall not be liable to you or to any third party for any modification, suspension, or discontinuance of the Service.
            </p>
          </section>
          
          <section className="space-y-4">
            <h2 className="text-xl font-semibold">3. User Accounts</h2>
            <p>
              To access certain features of the Service, you may be required to register for an account. You agree to provide accurate, current, and complete information during the registration process and to update such information to keep it accurate, current, and complete.
            </p>
            <p>
              You are responsible for safeguarding the password you use to access the Service and for any activities or actions under your password. We encourage you to use a "strong" password (a password that uses a combination of upper and lower case letters, numbers, and symbols) for your account.
            </p>
            <p>
              You agree not to disclose your password to any third party. You must notify us immediately upon becoming aware of any breach of security or unauthorized use of your account.
            </p>
          </section>
          
          <section className="space-y-4">
            <h2 className="text-xl font-semibold">4. Subscription and Payment</h2>
            <p>
              Some aspects of the Service are offered on a subscription basis. Payment terms vary based on the subscription plan you select:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Free Plan: Limited access to basic features at no cost</li>
              <li>Pro Plan: Access to advanced features for a monthly fee</li>
              <li>Business Plan: Enterprise-level features for a monthly fee</li>
            </ul>
            <p>
              When you purchase a subscription, you authorize us to charge the applicable fees to your designated payment method. Subscription fees are billed in advance on a monthly basis. You can cancel your subscription at any time, but no refunds will be provided for any unused portion of the subscription period.
            </p>
          </section>
          
          <section className="space-y-4">
            <h2 className="text-xl font-semibold">5. Acceptable Use</h2>
            <p>
              You agree not to use the Service to:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe the intellectual property rights of others</li>
              <li>Transmit any material that is harmful, offensive, or otherwise objectionable</li>
              <li>Impersonate any person or entity</li>
              <li>Interfere with or disrupt the Service or servers or networks connected to the Service</li>
              <li>Attempt to gain unauthorized access to any portion of the Service</li>
              <li>Use the Service for any fraudulent or illegal purpose</li>
            </ul>
          </section>
          
          <section className="space-y-4">
            <h2 className="text-xl font-semibold">6. Intellectual Property</h2>
            <p>
              The Service and its original content, features, and functionality are and will remain the exclusive property of FraudEye and its licensors. The Service is protected by copyright, trademark, and other laws of both the United States and foreign countries.
            </p>
            <p>
              Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of FraudEye.
            </p>
          </section>
          
          <section className="space-y-4">
            <h2 className="text-xl font-semibold">7. Limitation of Liability</h2>
            <p>
              IN NO EVENT SHALL FRAUDEYE, ITS OFFICERS, DIRECTORS, EMPLOYEES, OR AGENTS, BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR ACCESS TO OR USE OF OR INABILITY TO ACCESS OR USE THE SERVICE.
            </p>
          </section>
          
          <section className="space-y-4">
            <h2 className="text-xl font-semibold">8. Disclaimer of Warranties</h2>
            <p>
              THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT ANY WARRANTIES OF ANY KIND. FRAUDEYE EXPRESSLY DISCLAIMS ALL WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
          </section>
          
          <section className="space-y-4">
            <h2 className="text-xl font-semibold">9. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the State of California, without regard to its conflict of law provisions.
            </p>
          </section>
          
          <section className="space-y-4">
            <h2 className="text-xl font-semibold">10. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. If we make changes, we will provide notice by posting the updated Terms on this page and updating the "Last Updated" date. Your continued use of the Service after any such changes constitutes your acceptance of the new Terms.
            </p>
          </section>
          
          <section className="space-y-4">
            <h2 className="text-xl font-semibold">11. Contact Us</h2>
            <p>
              If you have any questions about these Terms, please contact us at:
            </p>
            <div className="mt-2">
              <p>Email: legal@fraudeye.com</p>
              <p>Address: 123 Fraud Detection Street, Analytics City, AC 12345</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
